const INPUT_NODE_INLINE_TOKEN_REGEXP = /\[@(input_image|input_video|input_file)(?::#\d*)?:([^\]]*)\]/g;

type InputNodeInlineTokenReplaceMode = 'value' | 'script-url';

/**
 * 判断字符串是否包含 input 节点 inline token。
 */
export function hasInputNodeInlineToken(promptValue: string): boolean {
  if (!promptValue) return false;
  INPUT_NODE_INLINE_TOKEN_REGEXP.lastIndex = 0;
  return INPUT_NODE_INLINE_TOKEN_REGEXP.test(promptValue);
}

/**
 * 删除上游输入节点时，同步清理 prompt 中引用这些节点的 inline token。
 */
export function removeInputNodeInlineTokens(promptValue: string, targetNodeIds: string[]) {
  if (!promptValue || !targetNodeIds.length) return promptValue;
  const targetSet = new Set(targetNodeIds);
  return promptValue.replace(INPUT_NODE_INLINE_TOKEN_REGEXP, (token, _tag: string, index: string) => (
    targetSet.has(index) ? '' : token
  ));
}

/**
 * 删除 prompt 中全部 input_image/input_video/input_file 引用 token（单节点复制时使用）。
 */
export function removeAllInputNodeInlineTokens(promptValue: string): string {
  if (!promptValue) return promptValue;
  return promptValue.replace(INPUT_NODE_INLINE_TOKEN_REGEXP, '');
}

/**
 * 节点 ID 重映射时，同步替换 prompt 中 [@input_image:id] 等 inline token 的 id 部分。
 * 批量复制时若引用节点未随副本一起复制（id 不在 copiedNodeIds 内），则删除对应 token。
 */
export function remapInputNodeInlineTokens(
  promptValue: string,
  nodeReplaceMap: Map<string, string>,
  copiedNodeIds?: ReadonlySet<string>,
) {
  if (!promptValue || nodeReplaceMap.size === 0) return promptValue;

  return promptValue.replace(INPUT_NODE_INLINE_TOKEN_REGEXP, (token, _tag: string, index: string) => {
    if (copiedNodeIds && !copiedNodeIds.has(index)) {
      return '';
    }
    const nextId = nodeReplaceMap.get(index);
    if (!nextId || nextId === index) return token;
    return token.replace(`:${index}]`, `:${nextId}]`);
  });
}

/**
 * 将输入节点 inline token 替换为传入的引用文案，不做 URL 数组包装。
 */
function replaceInputNodeInlineTokensWithValues(
  promptValue: string,
  replacementByNodeId: ReadonlyMap<string, string>,
) {
  if (!promptValue || replacementByNodeId.size === 0) return promptValue;

  return promptValue.replace(INPUT_NODE_INLINE_TOKEN_REGEXP, (token, _tag: string, nodeId: string) => (
    replacementByNodeId.get(nodeId) || token
  ));
}

/**
 * 递归替换执行参数中的输入节点 inline token，覆盖 prompt、summary、description 等字符串字段。
 */
function replaceInputNodeInlineTokenValue(
  value: unknown,
  replacementByNodeId: ReadonlyMap<string, string>,
  mode: InputNodeInlineTokenReplaceMode,
): unknown {
  if (typeof value === 'string') {
    return mode === 'script-url'
      ? replaceInputImageInlineTokensWithUrls(value, replacementByNodeId)
      : replaceInputNodeInlineTokensWithValues(value, replacementByNodeId);
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceInputNodeInlineTokenValue(item, replacementByNodeId, mode));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, item]) => {
      result[key] = replaceInputNodeInlineTokenValue(item, replacementByNodeId, mode);
      return result;
    }, {});
  }
  return value;
}

/**
 * 在不修改节点保存参数的前提下，生成仅用于提交接口的 token 替换后参数。
 */
export function replaceInputNodeInlineTokenParameterValues<T>(
  value: T,
  replacementByNodeId: ReadonlyMap<string, string>,
  mode: InputNodeInlineTokenReplaceMode = 'value',
): T {
  if (replacementByNodeId.size === 0) return value;
  return replaceInputNodeInlineTokenValue(value, replacementByNodeId, mode) as T;
}

/**
 * 提交脚本执行参数时，将已知输入节点引用替换为脚本兼容的资源 URL 数组字符串。
 */
export function replaceInputImageInlineTokensWithUrls(
  promptValue: string,
  mediaUrlByNodeId: ReadonlyMap<string, string>,
) {
  if (!promptValue || mediaUrlByNodeId.size === 0) return promptValue;

  return promptValue.replace(INPUT_NODE_INLINE_TOKEN_REGEXP, (token, _tag: string, nodeId: string) => {
    const mediaUrl = mediaUrlByNodeId.get(nodeId);
    if (!mediaUrl) return token;
    const escapedMediaUrl = mediaUrl.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    return `['${escapedMediaUrl}']`;
  });
}
